import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate } from 'react-router-dom'
import { Button } from 'components/common'
import { PAGE_DASHBOARD } from 'utils/constants'
import logo from 'assets/img/logo.png'

const WifiOffIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-12 w-12"
  >
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
    <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
    <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
    <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <circle cx="12" cy="20" r="1" fill="currentColor" />
  </svg>
)

const ServerDownIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-12 w-12"
  >
    <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
    <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" strokeWidth={3} />
    <line x1="6" y1="18" x2="6.01" y2="18" strokeWidth={3} />
    <line x1="14" y1="15" x2="20" y2="21" />
    <line x1="20" y1="15" x2="14" y2="21" />
  </svg>
)

const ServerError = () => {
  const navigate = useNavigate()
  const [isRetrying, setIsRetrying] = useState(false)
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [retryFailed, setRetryFailed] = useState(false)

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false)
      handleRetry()
    }
    const handleOffline = () => setIsOffline(true)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const handleRetry = async () => {
    if (isRetrying) return
    setIsRetrying(true)
    setRetryFailed(false)

    try {
      if (!navigator.onLine) {
        setIsOffline(true)
        setRetryFailed(true)
        return
      }

      const serverUrl =
        process.env.REACT_APP_SERVER_URL || 'http://localhost:5000'

      await fetch(`${serverUrl}`, {
        method: 'GET',
        cache: 'no-store',
        signal: AbortSignal.timeout(5000),
      })

      window.location.replace(PAGE_DASHBOARD.path)
    } catch {
      setRetryFailed(true)
    } finally {
      setIsRetrying(false)
    }
  }

  const handleGoHome = () => {
    navigate(PAGE_DASHBOARD.path)
  }

  const title = isOffline ? 'No Internet Connection' : 'Server Unavailable'

  const description = isOffline
    ? 'Your device appears to be offline. Please check your network or Wi-Fi connection and try again.'
    : "We can't reach the server right now. It may be temporarily down or unreachable. Please wait a moment and try again."

  return (
    <>
      <Helmet>
        <title>GAG Aggregator | Connection Error</title>
      </Helmet>

      <div className="flex min-h-screen w-full flex-col bg-[#f7f7f7]">
        {/* Header */}
        <header className="flex h-16 shrink-0 items-center justify-end border-b border-gray-200 bg-white px-8">
          <div className="w-24 shrink-0">
            <img
              src={logo}
              alt="gag-logo"
              className="h-full w-full object-contain"
            />
          </div>
        </header>

        {/* Content */}
        <main className="flex flex-1 items-center justify-center px-4 py-12">
          <div className="w-full max-w-lg">
            {/* Card */}
            <div className="rounded-2xl border border-gray-100 bg-white p-10 shadow-md">
              {/* Icon */}
              <div
                className={`mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full ${
                  isOffline
                    ? 'bg-orange-50 text-orange-500'
                    : 'bg-red-50 text-error'
                }`}
              >
                {isOffline ? <WifiOffIcon /> : <ServerDownIcon />}
              </div>

              {/* Status badge */}
              <div className="mb-4 flex justify-center">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
                    isOffline
                      ? 'bg-orange-50 text-orange-600'
                      : 'bg-red-50 text-error'
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 animate-pulse rounded-full ${
                      isOffline ? 'bg-orange-500' : 'bg-error'
                    }`}
                  />
                  {isOffline ? 'Offline' : 'Server Unreachable'}
                </span>
              </div>

              {/* Heading */}
              <h1 className="mb-3 text-center text-2xl font-bold text-primary">
                {title}
              </h1>

              {/* Description */}
              <p className="mb-8 text-center text-sm leading-relaxed text-sidebar-secondary">
                {description}
              </p>

              {/* Retry failure notice */}
              {retryFailed && !isRetrying && (
                <div className="mb-6 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-center text-xs text-error">
                  Still unable to connect. Please check your connection and try
                  again.
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                <Button
                  buttonText={isRetrying ? 'Connecting...' : 'Retry Connection'}
                  variant="filled"
                  size="medium"
                  loading={isRetrying}
                  type="button"
                  className="w-full sm:w-44"
                  onClick={handleRetry}
                />
                <Button
                  buttonText="Go to Home"
                  variant="outline"
                  size="medium"
                  type="button"
                  className="w-full sm:w-44"
                  onClick={handleGoHome}
                />
              </div>
            </div>

            {/* Tips */}
            <div className="mt-6 rounded-xl border border-gray-100 bg-white px-6 py-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-sidebar-secondary">
                Troubleshooting Tips
              </p>
              <ul className="space-y-1.5 text-xs text-sidebar-secondary">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 shrink-0 text-gray-400">•</span>
                  Check that you are connected to a Wi-Fi or ethernet network.
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 shrink-0 text-gray-400">•</span>
                  Ensure the backend server is running and accessible.
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 shrink-0 text-gray-400">•</span>
                  Try refreshing the page
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5 shrink-0 text-gray-400">•</span>
                  Contact your system administrator if the issue persists.
                </li>
              </ul>
            </div>
          </div>
        </main>
      </div>
    </>
  )
}

export default ServerError
